import { CustomButton } from "@/components/ui/Buttons";
import Graphic from "@/assets/images/assignment/assignment-graphic.webp";
import GraphicMobile from "@/assets/images/assignment/assignment-graphic-small.webp";
import UploadIcon from "@/assets/icons/UploadWithoutCircle.svg";
import PlayIcon from "@/assets/icons/PlayIcon.svg";
import { formatFileSize } from "@/utils/formatFileSize";
import LoadingStrip from "@/components/ui/LoadinStrip";
import Attachment from "@/assets/icons/Attachment.svg";
import { useAppSelector } from "@/redux/hooks/hooks";
import DeleteIcon from "@/assets/icons/DeleteIconBlack.svg";
import {
  useGetCreatorStatusQuery,
  useGetMeQuery,
} from "@/redux/feature/user/userApi";
import { useState } from "react";
import { FileDetails } from "@/types/types";
import toast from "react-hot-toast";
import { handleApiRequest } from "@/utils/handleApiRequest";
import {
  useGetAssignmentDeadlineQuery,
  useSubmitAssignmentMutation,
} from "@/redux/feature/assignment/assignmentApi";
import { useNavigate } from "react-router-dom";
import { FileWithPath, useDropzone } from "react-dropzone";
import { UploadFileToS3 } from "@/utils/uploadFilestoS3";
import { TCreatorStatus } from "@/redux/feature/user/userType";
import moment from "moment";

const AssignmentSubmissionModal = () => {
  const [files, setFiles] = useState<FileDetails[]>([]);
  const [showProgress, setShowProgress] = useState<string | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);
  const user = useAppSelector((state) => state.auth.user);
  const { data } = useGetMeQuery(user?.email);
  const [isLoading, setIsLoading] = useState(false);

  // Assignment status
  const { data: CreatorStatus } = useGetCreatorStatusQuery(
    user?.email as string,
    {
      skip: !user?.email,
      refetchOnMountOrArgChange: true,
    }
  );

  const creatorStatus: TCreatorStatus = CreatorStatus?.data;

  const isAssignmentPending =
    !creatorStatus?.assignmentStatus?.isRejected &&
    !creatorStatus?.assignmentStatus?.isApproved &&
    creatorStatus?.assignmentStatus?.isInitiated;

  // Video upload
  const userDetails = data?.data;
  const maxFiles = 10;
  const maxSize = 100 * 1024 * 1024; // 100MB
  const accept = {
    "video/mp4": [],
  };

  const onDrop = async (acceptedFiles: FileWithPath[]) => {
    if (uploading) return;
    setUploading(true);

    const newFiles = acceptedFiles.slice(0, maxFiles - files.length);
    const uniqueNumber = new Date().getTime();

    let tempFiles: FileDetails[] = [];

    for (const file of newFiles) {
      // Check file size
      if (file.size > maxSize) {
        toast.error("File size exceeds the 100MB limit.");
        setUploading(false);
        return; // Stop processing further if file size is too large
      }

      // Check file type
      if (file.type !== "video/mp4") {
        toast.error("Only MP4 files are allowed.");
        setUploading(false);
        return; // Stop processing further if file type is invalid
      }
      const fileDetails: FileDetails = {
        name: `${uniqueNumber.toString()}-${file.name}`,
        size: file.size,
        type: file.type,
        lastModified: file.lastModified,
        webkitRelativePath: file.webkitRelativePath,
        local_url: URL.createObjectURL(file),
        live_url: null,
      };

      tempFiles.push(fileDetails);
    }

    // Batch update the state once at the end
    setFiles((prevFiles) => [...prevFiles, ...tempFiles]);

    for (const fileDetails of tempFiles) {
      try {
        setShowProgress(fileDetails.name);
        const uploadedFile = await UploadFileToS3(fileDetails);

        setFiles((prevFiles) =>
          prevFiles.map((f) =>
            f.name === fileDetails.name
              ? {
                  ...fileDetails,
                  live_url: uploadedFile.live_url,
                  uploading: false,
                }
              : f
          )
        );
        setShowProgress(null);
      } catch (error) {
        setShowProgress(null);
        console.error(`Failed to upload file: ${fileDetails.name}`, error);
      }
    }

    setUploading(false);
  };

  const handleRemoveFile = (index: number) => {
    const updatedFiles = files.filter((_, i) => i !== index);
    setUploading(false);
    setFiles(updatedFiles);
  };

  // ADD DROPZONE SETUP HERE
  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    multiple: true,
    maxSize,
    accept,
    noClick: true,
    onDropRejected(fileRejections, event) {
      for (const rejection of fileRejections) {
        for (const error of rejection.errors) {
          if (error.code === "file-too-large") {
            toast.error("File size exceeds the 100MB limit.");
          } else if (error.code === "file-invalid-type") {
            toast.error("Only MP4 files are allowed.");
          } else {
            toast.error(error.message); // fallback
          }
        }
      }
    },
  });

  const [loading, setLoading] = useState(false);

  // Submit the assignment
  const [submitAssignment] = useSubmitAssignmentMutation();
  const navigate = useNavigate();

  const handleSubmit = async () => {
    const isFilesUploadedProperly = files.every(
      (item) => item.live_url !== null
    );

    if (!isFilesUploadedProperly) {
      toast.error("Please wait until all files are uploaded.");
      return;
    }
    const payload = {
      email: userDetails?.email,
      submittedVideo: files,
      title: "Self Introduction: Passionbits Anchor Talent",
      description: "Self Introduction: Passionbits Anchor Talent",
    };
    setIsLoading(true);
    await handleApiRequest({
      payload: payload,
      requestFunction: submitAssignment,
      onSuccess: () => {
        navigate("/");
        setIsLoading(false);
        toast.success("Assignment Submitted Successfully");
      },
      onError: () => {
        setIsLoading(false);
        toast.error("Something Went Wrong");
      },
    });
  };

  //  handle the submission date

  const { data: AssignmentDeadline } = useGetAssignmentDeadlineQuery(
    user?.email
  );
  const formatDeadline = (deadline: string): string => {
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return moment(deadline).tz(userTimeZone).format("Do MMMM, YYYY - hh:mm a");
  };
  const deadline = AssignmentDeadline?.data?.deadline
    ? formatDeadline(AssignmentDeadline?.data?.deadline)
    : "Loading...";

  const handleCancel = () => {
    setFiles([]);
    setShowProgress(null);
    setUploading(false);

    // Reset the file input to allow re-uploading the same file
    const input = document.querySelector(
      'input[type="file"]'
    ) as HTMLInputElement;
    if (input) {
      input.value = "";
    }
  };

  return (
    <section {...getRootProps()}>
      {" "}
      {/* ADD getRootProps HERE */}
      <input {...getInputProps()} /> {/* ADD getInputProps HERE */}
      {files.length > 0 ? (
        // Show only files and action buttons when files are uploaded
        <>
          <h2 className="text-[#101828] text-lg font-semibold mb-4">
            Uploaded Files
          </h2>

          <div className="space-y-3 mb-6">
            {files.map((file, index) => (
              <div
                key={index}
                className="border border-[#E4E7EC] w-full flex justify-between items-start rounded-lg p-4 bg-[#F9FAFB]"
              >
                <div className="flex w-full gap-x-[14px] items-start">
                  <div>
                    <Attachment />
                  </div>
                  <div className="w-full">
                    <p className="font-medium text-[#344054] text-sm">
                      {file?.name?.length > 40
                        ? `${file?.name?.slice(0, 40)}...`
                        : file?.name}
                    </p>
                    <p className="text-[#475467] text-xs mt-1">
                      {formatFileSize(file.size)}
                    </p>
                    <div className="pt-2">
                      {showProgress === file.name && <LoadingStrip />}
                    </div>
                    {file.live_url && (
                      <a
                        href={file.live_url}
                        target="_blank"
                        className="text-xs font-medium text-[#FF6600] hover:underline pt-1 inline-block"
                        rel="noopener noreferrer"
                      >
                        View file
                      </a>
                    )}
                  </div>
                </div>
                <div className="flex justify-end w-fit relative">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveFile(index);
                    }}
                    className="hover:opacity-70 transition-opacity"
                  >
                    <DeleteIcon />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 justify-end">
            <CustomButton
              onClick={handleCancel}
              variant="outline"
              color="#344054"
              fontSize="14px"
              fontWeight="600"
              border="1px solid #D0D5DD"
              borderRadius="8px"
              padding="10px 24px"
              className="leading-5 hover:bg-[#F9FAFB] transition-colors"
            >
              Cancel
            </CustomButton>

            <CustomButton
              onClick={handleSubmit}
              disabled={
                uploading || isLoading || files.some((f) => !f.live_url)
              }
              color="#FFFFFF"
              fontSize="14px"
              fontWeight="600"
              backgroundColor="#FF6600"
              borderRadius="8px"
              padding="10px 24px"
              className="leading-5 hover:bg-[#e65c00] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? "Submitting..." : "Submit Assignment"}
            </CustomButton>
          </div>
        </>
      ) : (
        // Show initial upload interface
        <>
          {/* Header Text */}
          <p className="text-[#101828B2] md:text-black text-start md:text-center text-[13px] font-medium mb-8 md:mb-6 tracking-[-1%]">
            Complete this task and upload your video to start landing gigs via
            Passionbits.
          </p>

          {/* Assignment Card */}
          <div className="border border-[#E4E7EC] p-2 rounded-lg flex flex-col md:flex-row items-center gap-4">
            {/* Desktop Image */}
            <img
              src={Graphic}
              alt="Passionbits creator assignment"
              className="hidden md:block w-[193px] h-[168px] object-cover rounded-md"
            />

            {/* Mobile Image */}
            <img
              src={GraphicMobile}
              alt="Passionbits creator assignment"
              className="md:hidden w-full h-[136px] object-contain rounded-md"
            />

            {/* Description */}
            <div>
              <h6 className="text-[#101828] text-sm font-grotesk font-bold tracking-normal">
                30s–1min{" "}
                <span className="font-normal">demo — product or app</span>
              </h6>

              <p className="mt-2 text-[#101828B2] text-xs leading-relaxed">
                Film a short clip. Either show a product or explain an
                app/service like you would to a friend — what it is, who it's
                for, and one clear benefit. Screen peek optional.
              </p>

              {/* OR Divider (Mobile only) */}
              <div className="my-6 flex items-center gap-x-3 md:hidden">
                <div className="flex-1 border-t border-dashed border-[#E4E7EC]" />
                <span className="text-xs text-[#667085] font-medium">OR</span>
                <div className="flex-1 border-t border-dashed border-[#E4E7EC]" />
              </div>

              {/* Upload prompt */}
              <p className="mt-6 text-[#686868] text-xs font-medium">
                <span className="text-black">Got a UGC clip already?</span>{" "}
                Upload that instead 👍
              </p>
            </div>
          </div>

          {/* Divider */}
          <hr className="bg-[#E4E7EC] my-6 w-full" />

          <div className="flex flex-col md:flex-row items-center gap-2">
            {/* Tips Box */}
            <div className="bg-[#F7FFF4] py-[7px] px-3 rounded-lg">
              <h6 className="text-[#1D7100] text-[11px] font-medium">
                Submission Tips ✅
              </h6>
              <p className="text-[#979797] text-[12px] mt-1 leading-relaxed">
                hook in first 2s • face natural light • crisp audio • show +
                tell • add one clear benefit
              </p>
            </div>

            {/* Divider (Mobile only) */}
            <hr className="bg-[#E4E7EC] my-[17px] md:hidden w-full" />

            {/* Buttons */}
            <div className="flex justify-between gap-3 w-full md:w-auto">
              <CustomButton
                variant="outline"
                color="#686868"
                fontSize="14px"
                fontWeight="600"
                border="1px solid #E4E7EC"
                borderRadius="8px"
                padding="10px 15px"
                className="leading-5 flex gap-x-1 items-center hover:bg-[#F3F3F3] transition-colors"
              >
                <PlayIcon />
                Sample
              </CustomButton>

              <CustomButton
                onClick={() => {
                  const input = document.querySelector(
                    'input[type="file"]'
                  ) as HTMLInputElement;
                  if (input) input.click();
                }}
                color="#FFFFFF"
                fontSize="14px"
                fontWeight="600"
                backgroundColor="#FF6600"
                borderRadius="8px"
                padding="10px 36px"
                className="leading-5 flex gap-x-1 items-center hover:bg-[#e65c00] transition-colors"
              >
                <UploadIcon />
                Upload Video
              </CustomButton>
            </div>
          </div>

          {/* Footer Note */}
          <p className="text-[#686868] text-[10px] text-center mt-6 leading-relaxed">
            We use this submission to assess skills, build your portfolio, and
            share it with <span className="text-black">right clients.</span>
          </p>
        </>
      )}
    </section>
  );
};

export default AssignmentSubmissionModal;
